import React, { useState, useEffect } from 'react';
import licenseManager from '../utils/licenseManager.js';
import NFCScanModal from './NFCScanModal.jsx';
import ShareModal from './ShareModal.jsx';
import PromoCodeModal from './PromoCodeModal.jsx';
import authManager from '../utils/authManager.js';
import '../styles/PremiumGate.css';

const PremiumGate = ({ feature, children, fallback = null, onNavigate = null }) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showNFCScan, setShowNFCScan] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userNfcId, setUserNfcId] = useState(null);
  const [activationResult, setActivationResult] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      const access = await licenseManager.hasFeatureAccess(feature);
      setHasAccess(access);
      setLoading(false);
    };

    checkAccess();

    // Listen for license updates
    const handleLicenseUpdate = () => {
      checkAccess();
    };

    window.addEventListener('license-updated', handleLicenseUpdate);

    return () => {
      window.removeEventListener('license-updated', handleLicenseUpdate);
    };
  }, [feature]);

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleCloseModal = () => {
    setShowUpgradeModal(false);
  };

  const handleNFCUpgrade = () => {
    // Close upgrade modal and open NFC scan modal
    setShowUpgradeModal(false);
    setShowNFCScan(true);
  };

  const handleNFCScanSuccess = (data) => {
    console.log('✅ NFC Scan successful:', data);
    setActivationResult(data);
    setUserNfcId(data.nfcId);
    setShowNFCScan(false);

    // Show success and share options
    setTimeout(() => {
      setShowShareModal(true);
    }, 1000);
  };

  const handleNFCScanError = (error) => {
    console.error('❌ NFC Scan failed:', error);
    setShowNFCScan(false);
    // Show error message (could be improved with a proper error modal)
    alert(`NFC Scan failed: ${error}`);
  };

  const handleNFCScanClose = () => {
    setShowNFCScan(false);
  };

  const handleShareModalClose = () => {
    setShowShareModal(false);
  };

  const handleOpenShare = () => {
    setShowShareModal(true);
  };

  const handlePromoCodeClick = () => {
    setShowUpgradeModal(false);
    setShowPromoCode(true);
  };

  const handlePromoCodeClose = () => {
    setShowPromoCode(false);
  };

  if (loading) {
    return <div className="premium-loading">Loading...</div>;
  }

  // If user has access, show the content
  if (hasAccess) {
    return children;
  }

  // If fallback provided, show it
  if (fallback) {
    return fallback;
  }

  // Otherwise show premium gate
  const upgradeMessage = licenseManager.getUpgradeMessage(feature);


  return (
    <>
      <div className="premium-gate">
        <div className="premium-gate-content">
          <div className="premium-icon">🔒</div>
          <h3>{upgradeMessage.title}</h3>
          <p>{upgradeMessage.description}</p>
          <div className="premium-benefit">
            <span className="benefit-icon">✨</span>
            <span>{upgradeMessage.benefit}</span>
          </div>
          <button
            className="upgrade-btn"
            onClick={handleUpgradeClick}
          >
            🎫 Unlock with NFC
          </button>

          {onNavigate && (
            <button
              className="back-btn"
              onClick={() => onNavigate('home')}
            >
              ← Back to Home
            </button>
          )}
        </div>
      </div>

      {showUpgradeModal && (
        <div className="upgrade-modal-overlay">
          <div className="upgrade-modal">
            <div className="upgrade-modal-header">
              <h3>🎫 Unlock Premium Features</h3>
              <button
                className="close-btn"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>

            <div className="upgrade-modal-body">
              <div className="nfc-upgrade">
                <div className="nfc-icon">📱</div>
                <p>Scan your DIAS merchandise NFC tag to unlock all premium features!</p>

                <button
                  className="nfc-scan-btn"
                  onClick={handleNFCUpgrade}
                  disabled={loading}
                >
                  📡 Unlock with NFC
                </button>

                <div className="premium-features-list">
                  <h4>Premium includes:</h4>
                  <ul>
                    <li>📄 Document Storage</li>
                    <li>🧳 Smart Luggage Lists</li>
                    <li>📊 PDF Trip Reports</li>
                    <li>🏆 Prize Challenges</li>
                    <li>☁️ Cloud Sync</li>
                  </ul>
                </div>

                <div className="premium-sharing">
                  <div className="sharing-divider">
                    <span>OR</span>
                  </div>

                  <button
                    className="promo-code-btn"
                    onClick={handlePromoCodeClick}
                  >
                    🎟️ Enter Promo Code
                  </button>

                  <button
                    className="share-app-btn"
                    onClick={handleOpenShare}
                    style={{marginTop: '10px'}}
                  >
                    🚀 Share App with Friends
                  </button>
                </div>

                <div className="merchandise-info">
                  <p><small>Don't have DIAS merchandise? <a href="#" style={{color: '#b8a082'}}>Get yours here!</a></small></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NFC Scan Modal */}
      <NFCScanModal
        isOpen={showNFCScan}
        onClose={handleNFCScanClose}
        onSuccess={handleNFCScanSuccess}
        onError={handleNFCScanError}
      />

      {/* Promo Code Modal */}
      <PromoCodeModal
        isOpen={showPromoCode}
        onClose={handlePromoCodeClose}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={handleShareModalClose}
        nfcId={userNfcId}
        userInfo={authManager.getCurrentUser()}
      />
    </>
  );
};

export default PremiumGate;